import { useEffect, useState } from "react";
import { CreditCard, IndianRupee, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";

const METHODS = ["all", "card", "upi", "cod"];

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("all");

  const load = () => {
    setLoading(true);
    const params = method === "all" ? {} : { method };
    api.get("/admin/payments", { params })
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load payments"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [method]);

  const total = items.reduce((s, i) => s + (i.status === "Captured" ? i.amount : 0), 0);
  const refunded = items.reduce((s, i) => s + (i.status === "Refunded" ? i.amount : 0), 0);

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="admin-payments">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <Tile label="Captured" value={`₹${total.toLocaleString("en-IN")}`} icon={CheckCircle2} accent="var(--green)" />
        <Tile label="Refunded" value={`₹${refunded.toLocaleString("en-IN")}`} icon={XCircle} accent="#9b2c2c" />
        <Tile label="Total Transactions" value={items.length} icon={CreditCard} accent="var(--gold)" />
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {METHODS.map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className={`px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-semibold rounded-full border transition ${
              method === m ? "bg-[var(--green)] text-white border-[var(--green)]" : "bg-white text-[var(--ink-2)] border-[#e5dec9] hover:border-[var(--green)]"
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#e5dec9] rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-[var(--bg-cream)] border-b border-[#e5dec9] text-[11px] uppercase tracking-widest text-[var(--ink-3)] flex items-center gap-2">
          <CreditCard size={13} /> Payment Ledger
          <span className="ml-auto text-[10px] normal-case tracking-normal text-[var(--ink-3)]">All payments are MOCKED · Real Stripe / Razorpay coming soon</span>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[var(--ink-3)] uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 w-12">S.No</th>
              <th className="text-left">Order</th>
              <th className="text-left">Customer</th>
              <th className="text-left">Date</th>
              <th className="text-center">Method</th>
              <th className="text-right">Amount</th>
              <th className="text-center px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-10 text-[var(--ink-3)] kbd text-xs">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-[var(--ink-3)] text-sm">No payments yet.</td></tr>}
            {items.map((p, idx) => (
              <tr key={p.id} className="border-t border-[#f0e7d3] hover:bg-[var(--bg-cream)]/40">
                <td className="px-4 py-3 kbd text-[0.7rem] text-[var(--ink-3)]">{idx + 1}</td>
                <td className="py-3 font-semibold text-[var(--ink)]">{p.order_no}</td>
                <td className="text-[var(--ink-2)]">{p.user_email}</td>
                <td className="text-[var(--ink-2)] text-xs">{fmt(p.created_at)}</td>
                <td className="text-center kbd text-[10px] uppercase">{p.method}</td>
                <td className="text-right font-semibold text-[var(--green)]">₹{p.amount}</td>
                <td className="text-center px-4">
                  {p.status === "Captured"
                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--green)] bg-green-50 border border-green-200 rounded-full px-2 py-1"><CheckCircle2 size={11} /> Captured</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-1"><XCircle size={11} /> Refunded</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#e5dec9] rounded-xl p-5 relative overflow-hidden">
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10" style={{ background: accent }} />
      <div className="kbd text-[0.6rem] text-[var(--ink-3)] flex items-center gap-1.5"><Icon size={11} /> {label}</div>
      <div className="font-serif-display text-3xl mt-2 text-[var(--ink)]">{value}</div>
    </div>
  );
}
